import { FunctionComponent, useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { providersApi, ApiError } from '../../../services/api';
import styles from './ScheduleAppointmentModal.module.css';

interface ScheduleAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId?: string;
  providerName: string;
  providerImage?: string;
  service?: string;
}

interface TimeSlot {
  time: string;
  available: boolean;
  appointmentType?: 'in-person' | 'video';
}

interface ProviderAvailability {
  date: string;
  slots: TimeSlot[];
}

const ScheduleAppointmentModal: FunctionComponent<ScheduleAppointmentModalProps> = ({
  isOpen,
  onClose,
  providerId,
  providerName,
  providerImage,
  service = 'General Consultation'
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [appointmentType, setAppointmentType] = useState<'in-person' | 'video'>('in-person');
  const [availability, setAvailability] = useState<ProviderAvailability[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen && providerId) {
      fetchProviderAvailability();
    }
  }, [isOpen, providerId, currentDate]);

  const fetchProviderAvailability = async () => {
    if (!providerId) return;
    
    setIsLoading(true);
    try {
      const startDate = new Date(currentDate);
      startDate.setDate(1);
      const endDate = new Date(currentDate);
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setDate(0);

      const result = await providersApi.getAvailability(providerId, {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      
      setAvailability(result);
    } catch (error) {
      console.error('Error fetching availability:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
      // Generate mock availability for demo
      generateMockAvailability();
    } finally {
      setIsLoading(false);
    }
  };

  const generateMockAvailability = () => {
    const mockAvailability: ProviderAvailability[] = [];
    const today = new Date();
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip weekends
      if (date.getDay() === 0 || date.getDay() === 6) continue;
      
      const slots: TimeSlot[] = [];
      const times = ['9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', 
                     '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'];
      
      times.forEach(time => {
        // Randomly make some slots unavailable
        const available = Math.random() > 0.3;
        // Morning slots are in-person, afternoon can be video
        const isAfternoon = time.includes('PM') && !time.includes('12:');
        slots.push({
          time,
          available,
          appointmentType: isAfternoon && Math.random() > 0.5 ? 'video' : 'in-person'
        });
      });
      
      mockAvailability.push({
        date: date.toISOString(),
        slots
      });
    }
    
    setAvailability(mockAvailability);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const navigateMonth = (direction: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const isDateAvailable = (date: Date | null) => {
    if (!date) return false;
    const dateStr = date.toISOString().split('T')[0];
    const dayAvailability = availability.find(a => a.date.startsWith(dateStr));
    return dayAvailability && dayAvailability.slots.some(s => s.available);
  };

  const isDateInPast = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateSelect = (date: Date | null) => {
    if (!date || isDateInPast(date) || !isDateAvailable(date)) return;
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const getTimeSlotsForDate = () => {
    if (!selectedDate) return [];
    const dateStr = selectedDate.toISOString().split('T')[0];
    const dayAvailability = availability.find(a => a.date.startsWith(dateStr));
    return dayAvailability?.slots || [];
  };

  const handleTimeSelect = (time: string, type: 'in-person' | 'video' | undefined) => {
    setSelectedTime(time);
    if (type) {
      setAppointmentType(type);
    }
  };

  const handleConfirmAppointment = async () => {
    if (!selectedDate || !selectedTime || !providerId) return;
    
    try {
      const appointmentData = {
        date: selectedDate.toISOString(),
        time: selectedTime,
        type: appointmentType,
        service,
        reason,
        notes
      };
      
      await providersApi.scheduleAppointment(providerId, appointmentData);
      
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error booking appointment:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
        // Could add error state/message display here
      }
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setShowConfirmation(false);
    setReason('');
    setNotes('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={handleClose} />
      <div className={styles.modalContent}>
        {!showConfirmation ? (
          <>
            {/* Header */}
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Schedule Appointment</h2>
              <button className={styles.closeButton} onClick={handleClose}>
                <CloseIcon />
              </button>
            </div>

            {/* Provider Info */}
            <div className={styles.providerInfo}>
              <img 
                src={providerImage || '/images/provider-placeholder.svg'} 
                alt={providerName}
                className={styles.providerImage}
              />
              <div className={styles.providerDetails}>
                <h3 className={styles.providerName}>{providerName}</h3>
                <p className={styles.serviceType}>{service}</p>
              </div>
            </div>

            {/* Calendar */}
            <div className={styles.calendarSection}>
              <div className={styles.calendarHeader}>
                <button 
                  className={styles.monthNavButton} 
                  onClick={() => navigateMonth(-1)}
                  disabled={currentDate.getMonth() === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear()}
                >
                  <ChevronLeftIcon />
                </button>
                <h3 className={styles.monthYear}>{formatMonthYear(currentDate)}</h3>
                <button className={styles.monthNavButton} onClick={() => navigateMonth(1)}>
                  <ChevronRightIcon />
                </button>
              </div>

              <div className={styles.weekDays}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className={styles.weekDay}>{day}</div>
                ))}
              </div>

              <div className={styles.calendarGrid}>
                {getDaysInMonth().map((date, index) => (
                  <div
                    key={index}
                    className={`${styles.calendarDay} ${
                      date && isDateAvailable(date) && !isDateInPast(date) ? styles.available : ''
                    } ${
                      date && selectedDate && date.toDateString() === selectedDate.toDateString() ? styles.selected : ''
                    } ${
                      date && isDateInPast(date) ? styles.past : ''
                    }`}
                    onClick={() => handleDateSelect(date)}
                  >
                    {date && (
                      <>
                        <span className={styles.dayNumber}>{date.getDate()}</span>
                        {isDateAvailable(date) && !isDateInPast(date) && (
                          <span className={styles.availableDot} />
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Time Slots */}
            {selectedDate && (
              <div className={styles.timeSlotsSection}>
                <h3 className={styles.sectionTitle}>
                  Available times for {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h3>
                
                {isLoading ? (
                  <div className={styles.loadingSlots}>Loading available times...</div>
                ) : (
                  <div className={styles.timeSlots}>
                    {getTimeSlotsForDate().map((slot, index) => (
                      <button
                        key={index}
                        className={`${styles.timeSlot} ${
                          !slot.available ? styles.unavailable : ''
                        } ${
                          selectedTime === slot.time ? styles.selected : ''
                        }`}
                        onClick={() => slot.available && handleTimeSelect(slot.time, slot.appointmentType)}
                        disabled={!slot.available}
                      >
                        <span className={styles.slotTime}>{slot.time}</span>
                        {slot.available && slot.appointmentType === 'video' && (
                          <span className={styles.videoTag}>Video</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Appointment Details */}
            {selectedDate && selectedTime && (
              <div className={styles.appointmentDetails}>
                <h3 className={styles.sectionTitle}>Appointment Details</h3>
                
                <div className={styles.appointmentTypeSelector}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="appointmentType"
                      value="in-person"
                      checked={appointmentType === 'in-person'}
                      onChange={(e) => setAppointmentType(e.target.value as 'in-person')}
                    />
                    <LocalHospitalIcon />
                    <span>In-Person Visit</span>
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="appointmentType"
                      value="video"
                      checked={appointmentType === 'video'}
                      onChange={(e) => setAppointmentType(e.target.value as 'video')}
                    />
                    <PersonIcon />
                    <span>Video Visit</span>
                  </label>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Reason for visit</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder="e.g., Follow-up appointment, Annual checkup"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Additional notes (optional)</label>
                  <textarea
                    className={styles.formTextarea}
                    placeholder="Any special requirements or symptoms to discuss"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Footer */}
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={handleClose}>
                Cancel
              </button>
              <button 
                className={styles.confirmButton}
                onClick={handleConfirmAppointment}
                disabled={!selectedDate || !selectedTime}
              >
                Confirm Appointment
              </button>
            </div>
          </>
        ) : (
          /* Confirmation Screen */
          <div className={styles.confirmationScreen}>
            <div className={styles.confirmationIcon}>
              <CheckCircleIcon style={{ fontSize: 64, color: '#10b981' }} />
            </div>
            <h2 className={styles.confirmationTitle}>Appointment Confirmed!</h2>
            <div className={styles.confirmationDetails}>
              <div className={styles.confirmationRow}>
                <CalendarMonthIcon />
                <span>{selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className={styles.confirmationRow}>
                <AccessTimeIcon />
                <span>{selectedTime}</span>
              </div>
              <div className={styles.confirmationRow}>
                <PersonIcon />
                <span>{providerName}</span>
              </div>
              <div className={styles.confirmationRow}>
                <LocalHospitalIcon />
                <span>{appointmentType === 'video' ? 'Video Visit' : 'In-Person Visit'}</span>
              </div>
            </div>
            <p className={styles.confirmationMessage}>
              You'll receive a confirmation email with appointment details and any preparation instructions.
            </p>
            <div className={styles.confirmationActions}>
              <button className={styles.addToCalendarButton}>
                Add to Calendar
              </button>
              <button className={styles.doneButton} onClick={handleClose}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ScheduleAppointmentModal;